import React from 'react';
import type { ComponentType, SVGProps } from 'react';
import { achievementIcon } from './gamification.utils';
import {
  GAMIFICATION_ACHIEVEMENT,
  GAMIFICATION_CARD,
  GAMIFICATION_CHIP,
  GAMIFICATION_CTA,
  GAMIFICATION_FOCUS_ACCENT,
  GAMIFICATION_METRIC,
  GAMIFICATION_PROGRESS,
  GAMIFICATION_RING,
  GAMIFICATION_TEXT,
} from './gamification.tokens';

export function GamificationSkeleton({ className = 'h-36' }: { className?: string }) {
  return (
    <div
      className={`animate-pulse rounded-2xl border border-slate-200/80 bg-slate-50/80 ${className}`}
      aria-hidden
    />
  );
}

export function GamificationCard({
  className = '',
  children,
  testId,
  variant = 'default',
}: {
  className?: string;
  children: React.ReactNode;
  testId?: string;
  variant?: 'default' | 'elevated' | 'identity' | 'momentum' | 'compact';
}) {
  const styles = {
    default: GAMIFICATION_CARD.base,
    elevated: GAMIFICATION_CARD.elevated,
    identity: GAMIFICATION_CARD.identity,
    momentum: GAMIFICATION_CARD.momentum,
    compact: GAMIFICATION_CARD.compact,
  };

  return (
    <section className={`overflow-hidden ${styles[variant]} ${className}`} data-testid={testId}>
      {children}
    </section>
  );
}

export function SectionHeader({
  title,
  description,
  action,
  className = '',
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex items-start justify-between gap-3 ${className}`}>
      <div className="min-w-0">
        <h2 className={GAMIFICATION_TEXT.title}>{title}</h2>
        {description ? <p className={`mt-0.5 ${GAMIFICATION_TEXT.body}`}>{description}</p> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

export function DataFootnote({ children }: { children: React.ReactNode }) {
  return <p className={GAMIFICATION_TEXT.footnote}>{children}</p>;
}

export function ProgressTrack({
  value,
  complete = false,
  size = 'md',
  className = '',
  showGoalGradient = true,
}: {
  value: number;
  complete?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  showGoalGradient?: boolean;
}) {
  const height = size === 'sm' ? 'h-1.5' : size === 'lg' ? 'h-3' : 'h-2';
  const pct = Math.min(100, Math.max(0, value));
  const nearComplete = showGoalGradient && pct >= 70 && pct < 100;

  return (
    <div className={`overflow-hidden rounded-full ${GAMIFICATION_PROGRESS.track} ${height} ${className}`}>
      <div
        className={`${height} rounded-full transition-all duration-700 ease-out ${
          complete
            ? GAMIFICATION_PROGRESS.fillComplete
            : nearComplete
              ? GAMIFICATION_PROGRESS.fillNear
              : GAMIFICATION_PROGRESS.fill
        }`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export function MomentumRing({
  level,
  xpInLevel,
  xpForNextLevel,
  size = 72,
  className = '',
}: {
  level: number;
  xpInLevel: number;
  xpForNextLevel: number;
  size?: number;
  className?: string;
}) {
  const stroke = 5;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = xpForNextLevel > 0 ? Math.min(1, xpInLevel / xpForNextLevel) : 1;
  const offset = circumference * (1 - pct);
  const gradientId = `momentum-ring-${level}-${size}`;

  return (
    <div
      className={`relative inline-flex shrink-0 items-center justify-center ${className}`}
      style={{ width: size, height: size }}
      aria-label={`Level ${level}, ${Math.round(pct * 100)}% progress to next level`}
    >
      <svg width={size} height={size} className="-rotate-90" aria-hidden>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={GAMIFICATION_RING.track}
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-all duration-700 ease-out"
        />
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={GAMIFICATION_RING.gradientStart} />
            <stop offset="100%" stopColor={GAMIFICATION_RING.gradientEnd} />
          </linearGradient>
        </defs>
      </svg>
      <span className="absolute text-sm font-bold tabular-nums text-slate-900">{level}</span>
    </div>
  );
}

export function LevelProgress({
  level,
  xpInLevel,
  xpForNextLevel,
  totalXp,
  className = '',
}: {
  level: number;
  xpInLevel: number;
  xpForNextLevel: number;
  totalXp?: number;
  className?: string;
}) {
  const pct = xpForNextLevel > 0 ? Math.round((xpInLevel / xpForNextLevel) * 100) : 100;
  const remaining = Math.max(0, xpForNextLevel - xpInLevel);

  return (
    <div className={`flex items-center gap-4 ${className}`}>
      <MomentumRing
        level={level}
        xpInLevel={xpInLevel}
        xpForNextLevel={xpForNextLevel}
        size={76}
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-slate-900">Seller level {level}</p>
        {totalXp != null ? (
          <p className="text-xs text-slate-500">{totalXp.toLocaleString()} activity points earned</p>
        ) : null}
        <ProgressTrack value={pct} size="sm" className="mt-2" />
        <p className="mt-1.5 text-xs text-slate-500">
          {remaining > 0 ? `${remaining} pts to level ${level + 1}` : `Level ${level + 1} unlocked soon`}
        </p>
      </div>
    </div>
  );
}

export function LevelBadge({ level, className = '' }: { level: number; className?: string }) {
  return (
    <span
      className={`inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-sm font-bold tabular-nums text-slate-900 ${className}`}
      aria-label={`Seller level ${level}`}
    >
      {level}
    </span>
  );
}

export function XpProgressRing(props: {
  level: number;
  xpInLevel: number;
  xpForNextLevel: number;
  size?: number;
  className?: string;
}) {
  return <MomentumRing {...props} size={props.size ?? 56} />;
}

export function AchievementIcon({
  code,
  category,
  unlocked = true,
  size = 'md',
}: {
  code: string;
  category: string;
  unlocked?: boolean;
  size?: 'sm' | 'md' | 'lg';
}) {
  const Icon = achievementIcon(code, category);
  const dims =
    size === 'lg'
      ? 'h-14 w-14 rounded-2xl'
      : size === 'md'
        ? 'h-11 w-11 rounded-xl'
        : 'h-9 w-9 rounded-lg';
  const iconSize = size === 'lg' ? 'h-6 w-6' : size === 'md' ? 'h-5 w-5' : 'h-4 w-4';

  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center ${
        unlocked ? GAMIFICATION_ACHIEVEMENT.unlocked : GAMIFICATION_ACHIEVEMENT.locked
      } ${dims}`}
      aria-hidden
    >
      <Icon className={iconSize} />
    </span>
  );
}

export function AchievementBadgeIcon({
  icon: _icon,
  unlocked = true,
  size = 'md',
}: {
  icon: string;
  unlocked?: boolean;
  size?: 'sm' | 'md' | 'lg';
}) {
  void _icon;
  return <AchievementIcon code="" category="journey" unlocked={unlocked} size={size} />;
}

export function MetricCell({
  label,
  value,
  delta,
  tone,
}: {
  label: string;
  value: string;
  delta?: string | null;
  tone?: 'emerald' | 'rose' | 'slate';
}) {
  const deltaClass =
    tone === 'emerald' ? 'text-emerald-700' : tone === 'rose' ? 'text-red-600' : 'text-slate-500';

  return (
    <div className={GAMIFICATION_METRIC}>
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-lg font-semibold tabular-nums tracking-tight text-slate-900">{value}</p>
      {delta ? <p className={`mt-0.5 text-xs font-medium ${deltaClass}`}>{delta} vs last week</p> : null}
    </div>
  );
}

export function StatusChip({
  children,
  variant = 'neutral',
}: {
  children: React.ReactNode;
  variant?: 'neutral' | 'success' | 'warning' | 'brand' | 'streak';
}) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${GAMIFICATION_CHIP[variant]}`}
    >
      {children}
    </span>
  );
}

export function FocusPanel({
  eyebrow,
  title,
  description,
  progress,
  progressLabel,
  momentumCopy,
  footnote,
  cta,
  urgency = 'normal',
  testId,
  embedded = false,
  tone = 'growth',
}: {
  eyebrow: React.ReactNode;
  title: string;
  description: string;
  progress?: number;
  progressLabel?: string;
  momentumCopy?: string | null;
  footnote?: string;
  cta: React.ReactNode;
  urgency?: 'high' | 'normal' | 'low';
  testId?: string;
  embedded?: boolean;
  tone?: 'business' | 'growth';
}) {
  const accent =
    tone === 'business'
      ? urgency === 'high'
        ? 'border-l-amber-500'
        : 'border-l-slate-300'
      : GAMIFICATION_FOCUS_ACCENT[urgency];

  return (
    <GamificationCard
      testId={testId}
      variant={embedded ? 'compact' : progress != null && progress >= 70 ? 'elevated' : 'default'}
      className={`border-l-4 ${accent}`}
    >
      <div className={embedded ? 'p-4' : 'p-5 sm:p-6'}>
        <p className={GAMIFICATION_TEXT.eyebrow}>{eyebrow}</p>
        <h3 className={`mt-1.5 text-lg font-semibold tracking-tight text-slate-900 ${embedded ? 'text-base' : ''}`}>
          {title}
        </h3>
        <p className={`mt-1 max-w-2xl ${GAMIFICATION_TEXT.body}`}>{description}</p>

        {progress != null ? (
          <div className="mt-4">
            <div className="mb-1.5 flex items-center justify-between gap-2 text-xs font-medium text-slate-600">
              <span>{progressLabel}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <ProgressTrack value={progress} size="md" />
            {momentumCopy ? <p className={`mt-2 ${GAMIFICATION_TEXT.momentum}`}>{momentumCopy}</p> : null}
          </div>
        ) : null}

        {footnote ? (
          <p
            className={`mt-3 text-xs font-medium ${tone === 'business' ? 'text-slate-500' : 'text-violet-700'} ${GAMIFICATION_TEXT.momentum}`}
          >
            {footnote}
          </p>
        ) : null}

        <div className={embedded ? 'mt-4' : 'mt-5'}>{cta}</div>
      </div>
    </GamificationCard>
  );
}

export { GAMIFICATION_CTA };

export function GamificationEyebrow({
  icon: Icon,
  children,
  accent: _accent,
}: {
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  children: React.ReactNode;
  accent?: string;
}) {
  void _accent;
  return (
    <p className={`flex items-center gap-1.5 ${GAMIFICATION_TEXT.eyebrow}`}>
      {Icon ? <Icon className="h-3.5 w-3.5" aria-hidden /> : null}
      {children}
    </p>
  );
}

export function GamificationProgressBar({
  value,
  complete = false,
  size = 'md',
  accent: _accent,
}: {
  value: number;
  complete?: boolean;
  size?: 'sm' | 'md';
  accent?: string;
}) {
  void _accent;
  return <ProgressTrack value={value} complete={complete} size={size} />;
}

export function GamificationStatPill(props: {
  label: string;
  value: string;
  delta?: string | null;
  tone?: 'emerald' | 'rose' | 'slate';
}) {
  return <MetricCell {...props} />;
}

export type GamificationAccent = string;
